import { Alert, Badge, Button, cardClass, fieldClass, selectClass, Table } from '@ledgerloom/ui';
import { useState, type FormEvent } from 'react';
import { api, propose } from '../api';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Field } from '../components/Field';
import { Empty, ErrorBanner, Icon, Loading, outcomeMessage, Time, useToast } from '../components/ui';
import { useApi, usePageTitle } from '../hooks';
import { accessScopes, scopeCovers } from '../session';
import { SettingsLayout, TIER_NAME, type User } from './Admin';

// Settings, Roles and access (ADR 0029): the workspace's roles, who holds
// which, and integrations with their API keys. A role or a person's role
// changes only as a signed change set (config.role); integrations and keys are
// added directly by an admin, as people are. Patterns: data-display/table,
// forms/checkbox for the permission grid, content-management/modal for keys.

type Tier = 'owner' | 'admin' | 'manager' | 'member';
type Reach = 'all' | 'managed' | 'team' | 'own';
interface Role { key: string; name: string; description: string | null; tier: Tier; scopes: string[]; reach: Reach; status: 'active' | 'archived'; built_in: boolean; holders: number }
interface Roles { roles: Role[]; tiers: Array<{ tier: Tier; scopes: string[]; reaches: Reach[] }>; scopes: string[] }
interface Key { id: string; name: string; scopes: string[]; expires_at: string; revoked_at: string | null; last_used_at: string | null }
interface Integration { id: string; name: string; status: 'active' | 'disabled'; role_key: string | null; role_name: string | null; scopes: string[]; answerable_user_id: string; answerable_name: string; created_at: string; keys: Key[] }

const REACH_TEXT: Record<Reach, string> = {
  all: 'Every contact',
  managed: 'Shared contacts, their own, their teammates\', and every contact of the teams they manage',
  team: 'Shared contacts, their own, and their teammates\'',
  own: 'Shared contacts and their own',
};
const VERBS: Array<[string, string]> = [['read', 'See'], ['propose', 'Propose'], ['approve', 'Approve'], ['configure', 'Change settings'], ['admin', 'Administer']];
const AREA: Record<string, string> = { me: 'Their own account', parties: 'Contacts', party: 'Contact' };
const area = (domain: string) => { const t = AREA[domain] ?? domain.replace(/_/g, ' '); return t.charAt(0).toUpperCase() + t.slice(1); };
const slug = (name: string) => name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').replace(/^(\d)/, 'r_$1').slice(0, 40);

export function RolesPage() {
  usePageTitle('Roles and access');
  return (
    <SettingsLayout title="Roles and access" lead="What each person can see and do, and the integrations that reach Mazu with API keys.">
      <RolesCard /><Integrations />
    </SettingsLayout>
  );
}

function RolesCard() {
  const roles = useApi<Roles>('/v1/roles');
  const users = useApi<User[]>('/v1/users');
  const toast = useToast();
  const [editing, setEditing] = useState<Role | null>(null);
  const [archiving, setArchiving] = useState<Role | null>(null);
  const [chosen, setChosen] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const assignable = roles.data?.roles.filter((r) => r.tier !== 'owner' && r.status === 'active') ?? [];

  async function send(payload: Record<string, unknown>, done: string) {
    setBusy(true); setError(null);
    try {
      const out = await propose('config.role', payload);
      toast(outcomeMessage(out, done));
      if (out.state !== 'rejected') { setEditing(null); setArchiving(null); setChosen({}); void roles.reload(); void users.reload(); }
      else setError(new Error(outcomeMessage(out)));
    } catch (e) { setError(e); } finally { setBusy(false); }
  }

  return (
    <>
      <section className={cardClass} aria-labelledby="roles-h">
        <div className="card-head">
          <div><h2 id="roles-h">Roles</h2><div className="faint">A role narrows one of the four built-in roles: what it sees, what it does, and whose contacts it sees. Changing one is a change set an admin signs.</div></div>
          {/* A new role starts as the built-in Member, area by area, for the admin to take away from. */}
          <Button type="submit" variant="secondary" size="sm" disabled={!roles.data} onClick={() => { setError(null); setEditing({ key: '', name: '', description: null, tier: 'member', scopes: roles.data!.scopes.filter((s) => scopeCovers(roles.data!.tiers.find((t) => t.tier === 'member')!.scopes, s)), reach: 'team', status: 'active', built_in: false, holders: 0 }); }}><Icon name="plus" />New role</Button>
        </div>
        {roles.error ? <div className="card-pad"><ErrorBanner error={roles.error} /></div> : !roles.data ? <Loading label="Loading roles" /> : (
          <Table label="Roles">
            <caption className="sr-only">Roles in this workspace</caption>
            <thead><tr><th scope="col">Role</th><th scope="col">Based on</th><th scope="col">Sees</th><th scope="col">Held by</th><th scope="col"><span className="sr-only">Actions</span></th></tr></thead>
            <tbody>{roles.data.roles.filter((r) => r.status === 'active').map((r) => (
              <tr key={r.key}>
                <td>{r.name}{r.built_in && <> <Badge>Built in</Badge></>}{r.description && <div className="faint small">{r.description}</div>}</td>
                <td className="small">{TIER_NAME[r.tier]}</td>
                <td className="small">{REACH_TEXT[r.reach]}</td>
                <td className="small">{r.holders === 1 ? '1 person' : `${r.holders} people`}</td>
                <td>{!r.built_in && <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                  <Button type="submit" variant="ghost" size="sm" aria-label={`Edit ${r.name}`} onClick={() => { setError(null); setEditing(r); }}>Edit</Button>
                  <Button type="submit" variant="ghost" size="sm" aria-label={`Archive ${r.name}`} disabled={r.holders > 0} title={r.holders > 0 ? 'Give its people another role first' : undefined} onClick={() => { setError(null); setArchiving(r); }}>Archive</Button>
                </div>}</td>
              </tr>
            ))}</tbody>
          </Table>
        )}
        {!editing && error ? <div className="card-pad"><ErrorBanner error={error} /></div> : null}
      </section>
      {editing && roles.data && <RoleEditor key={editing.key || 'new'} start={editing} catalog={roles.data} busy={busy} error={error}
        onCancel={() => { setEditing(null); setError(null); }}
        onSave={(r) => void send({ role: r, reason: editing.key ? `Change the ${r.name} role` : `A new role: ${r.name}` }, editing.key ? 'Role saved' : 'Role created')} />}
      <section className={cardClass} aria-labelledby="holders-h">
        <div className="card-head"><div><h2 id="holders-h">Who holds which role</h2><div className="faint">Giving someone a role is a change set; nobody signs a change to their own role alone.</div></div></div>
        {users.error ? <div className="card-pad"><ErrorBanner error={users.error} /></div> : !users.data ? <Loading label="Loading people" /> : (
          <Table label="Who holds which role">
            <caption className="sr-only">Each person's role</caption>
            <thead><tr><th scope="col">Person</th><th scope="col">Role</th><th scope="col"><span className="sr-only">Actions</span></th></tr></thead>
            <tbody>{users.data.filter((u) => u.status === 'active').map((u) => {
              const now = u.role_key ?? u.role;
              const pick = chosen[u.id] ?? now;
              return (
                <tr key={u.id}>
                  <td>{u.display_name}<div className="faint small">{u.email}</div></td>
                  <td>{u.role === 'owner' ? <Badge>Owner</Badge> : (
                    <select className={selectClass} aria-label={`Role for ${u.display_name}`} value={pick} onChange={(e) => setChosen({ ...chosen, [u.id]: e.target.value })}>
                      {assignable.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
                    </select>
                  )}</td>
                  <td>{u.role !== 'owner' && pick !== now && <Button type="submit" variant="secondary" size="sm" disabled={busy}
                    onClick={() => void send({ assign: [{ user_id: u.id, role: pick }], reason: `${u.display_name} becomes ${assignable.find((r) => r.key === pick)?.name ?? pick}` }, 'Role given')}>Give role<span className="sr-only"> to {u.display_name}</span></Button>}</td>
                </tr>
              );
            })}</tbody>
          </Table>
        )}
      </section>
      {archiving && (
        <ConfirmDialog open title={`Archive ${archiving.name}?`} confirmLabel="Archive role" tone="danger" busy={busy}
          onConfirm={() => void send({ role: { key: archiving.key, status: 'archived' }, reason: `Archive the ${archiving.name} role` }, 'Role archived')} onCancel={() => { setArchiving(null); setError(null); }}>
          <ErrorBanner error={error} focusOnShow />
          <p className="small" style={{ margin: 0 }}>Nobody holds it now. It stays in the log, and nobody can be given it again.</p>
        </ConfirmDialog>
      )}
    </>
  );
}

// The permission grid: one row per area, one column per verb. A box exists where the scope does, and is off where the role it is based on cannot hold it.
function RoleEditor({ start, catalog, busy, error, onCancel, onSave }: {
  start: Role; catalog: Roles; busy: boolean; error: unknown; onCancel: () => void;
  onSave: (r: { key: string; name: string; description?: string; tier: Tier; scopes: string[]; reach: Reach }) => void;
}) {
  const [name, setName] = useState(start.name);
  const [description, setDescription] = useState(start.description ?? '');
  const [tier, setTier] = useState<Tier>(start.tier);
  const [reach, setReach] = useState<Reach>(start.reach);
  const [scopes, setScopes] = useState<string[]>(start.scopes);
  const [problem, setProblem] = useState<string | null>(null);
  const ceiling = catalog.tiers.find((t) => t.tier === tier)!;
  const reaches = ceiling.reaches;
  const domains = [...new Set(catalog.scopes.map((s) => s.split(':')[1]!))].filter((d) => d !== '*').sort();
  const has = (s: string) => scopeCovers(scopes, s);
  const toggle = (s: string, on: boolean) => setScopes(on ? [...scopes, s] : scopes.filter((x) => x !== s));
  const changeTier = (t: Tier) => {
    const next = catalog.tiers.find((x) => x.tier === t)!;
    setTier(t);
    setScopes(scopes.filter((s) => scopeCovers(next.scopes, s)));
    if (!next.reaches.includes(reach)) setReach(next.reaches[0]!);
  };
  function save(e: FormEvent) {
    e.preventDefault();
    const key = start.key || slug(name);
    if (!name.trim() || key.length < 2) { setProblem('Give the role a name of at least two letters.'); return; }
    if (!scopes.length) { setProblem('Tick at least one thing the role can do.'); return; }
    setProblem(null);
    onSave({ key, name: name.trim(), ...(description.trim() ? { description: description.trim() } : {}), tier, scopes, reach });
  }
  return (
    <section className={cardClass} aria-labelledby="role-edit-h">
      <form onSubmit={save}>
        <div className="card-head"><h2 id="role-edit-h">{start.key ? `Edit ${start.name}` : 'New role'}</h2></div>
        <div className="card-pad form-grid">
          <div className="full"><ErrorBanner error={problem ? new Error(problem) : error} focusOnShow /></div>
          <Field label="Name" required><input className={fieldClass} autoComplete="off" maxLength={80} value={name} onChange={(e) => setName(e.target.value)} /></Field>
          <Field label="Based on" hint={start.holders > 0 ? 'People hold this role, so it stays on the role it is based on.' : 'A role never has more access than the role it is based on.'}>
            <select className={selectClass} value={tier} disabled={start.holders > 0} onChange={(e) => changeTier(e.target.value as Tier)}>
              <option value="member">Member</option><option value="manager">Manager</option><option value="admin">Admin</option>
            </select>
          </Field>
          <Field label="Description" className="full"><input className={fieldClass} autoComplete="off" maxLength={500} value={description} onChange={(e) => setDescription(e.target.value)} /></Field>
          <fieldset className="field full">
            <legend className="small muted">Whose contacts it sees</legend>
            {reaches.map((r) => <label key={r} className="row small"><input type="radio" name="reach" checked={reach === r} onChange={() => setReach(r)} />{REACH_TEXT[r]}</label>)}
          </fieldset>
        </div>
        <Table label="What the role can do">
          <caption className="sr-only">What the role can see and do, by area</caption>
          <thead><tr><th scope="col">Area</th>{VERBS.map(([, label]) => <th key={label} scope="col">{label}</th>)}</tr></thead>
          <tbody>
            <tr>
              <th scope="row">Everything, including areas added later</th>
              {VERBS.map(([verb, label]) => {
                const s = `${verb}:*`;
                const allowed = scopeCovers(ceiling.scopes, s);
                return <td key={verb}>{allowed && <input type="checkbox" aria-label={`${label}: everything`} checked={scopes.includes(s)} onChange={(e) => toggle(s, e.target.checked)} />}</td>;
              })}
            </tr>
            {domains.map((d) => (
              <tr key={d}>
                <th scope="row">{area(d)}</th>
                {VERBS.map(([verb, label]) => {
                  const s = `${verb}:${d}`;
                  if (!catalog.scopes.includes(s)) return <td key={verb} />;
                  const allowed = scopeCovers(ceiling.scopes, s);
                  const wild = scopes.includes(`${verb}:*`);
                  return <td key={verb}><input type="checkbox" aria-label={`${label}: ${area(d)}`} disabled={!allowed || wild} checked={allowed && has(s)} onChange={(e) => toggle(s, e.target.checked)} /></td>;
                })}
              </tr>
            ))}
          </tbody>
        </Table>
        <div className="card-pad row">
          <Button type="submit" disabled={busy}>{busy ? 'Sending…' : start.key ? 'Save role' : 'Create role'}</Button>
          <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        </div>
      </form>
    </section>
  );
}

function Integrations() {
  const list = useApi<Integration[]>('/v1/integrations');
  const users = useApi<User[]>('/v1/users');
  const toast = useToast();
  const [form, setForm] = useState({ name: '', answerable_user_id: '' });
  const [keyFor, setKeyFor] = useState<Integration | null>(null);
  const [keyName, setKeyName] = useState('');
  const [access, setAccess] = useState<'read' | 'propose'>('read');
  const [secret, setSecret] = useState<string | null>(null);
  const [disabling, setDisabling] = useState<Integration | null>(null);
  const [revoking, setRevoking] = useState<Key | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const people = users.data?.filter((u) => u.status === 'active') ?? [];
  const run = async (fn: () => Promise<void>) => { setBusy(true); setError(null); try { await fn(); void list.reload(); } catch (e) { setError(e); } finally { setBusy(false); } };

  const add = (e: FormEvent) => { e.preventDefault(); void run(async () => {
    await api('/v1/integrations', { body: form }); toast('Integration added'); setForm({ name: '', answerable_user_id: '' });
  }); };
  const makeKey = () => void run(async () => {
    const out = await api<{ token: string }>(`/v1/integrations/${keyFor!.id}/keys`, { body: { name: keyName, scopes: accessScopes(keyFor!.scopes, access), expires_in_days: 90 } });
    setSecret(out.token); setKeyFor(null); setKeyName('');
  });

  return (
    <section className={cardClass} aria-labelledby="integrations-h">
      <div className="card-head"><div><h2 id="integrations-h">Integrations</h2><div className="faint">For a script, a connector, or another company's AI. An integration never signs in and never approves: it reads, and what it sends waits for your rules like anyone's. <a href="/v1/docs">API reference</a></div></div></div>
      {secret && <div className="card-pad"><Alert tone="info" role="status"><strong>Copy this key now. It is not shown again.</strong><code className="note mono">{secret}</code><div><Button type="submit" variant="ghost" size="sm" onClick={() => setSecret(null)}>I saved it, hide it</Button></div></Alert></div>}
      {list.error ? <div className="card-pad"><ErrorBanner error={list.error} /></div> : !list.data ? <Loading label="Loading integrations" /> : !list.data.length ? <Empty title="No integrations yet">Add one to give a script or a connector its own API key, kept apart from any person's.</Empty> : (
        <Table label="Integrations">
          <caption className="sr-only">Integrations and their keys</caption>
          <thead><tr><th scope="col">Integration</th><th scope="col">Answers to</th><th scope="col">Role</th><th scope="col">Keys</th><th scope="col"><span className="sr-only">Actions</span></th></tr></thead>
          <tbody>{list.data.map((i) => (
            <tr key={i.id}>
              <td>{i.name}{i.status === 'disabled' && <> <Badge>Disabled</Badge></>}</td>
              <td className="small">{i.answerable_name}</td>
              <td className="small">{i.role_name ?? 'Member'}</td>
              <td className="small">{i.keys.filter((k) => !k.revoked_at).map((k) => (
                <div key={k.id} className="row" style={{ gap: 6 }}>
                  <span>{k.name}, {k.last_used_at ? <>used <Time iso={k.last_used_at} /></> : 'never used'}</span>
                  {i.status === 'active' && <Button type="submit" variant="ghost" size="sm" onClick={() => setRevoking(k)}>Revoke<span className="sr-only"> {k.name}</span></Button>}
                </div>
              ))}{!i.keys.some((k) => !k.revoked_at) && <span className="faint">None</span>}</td>
              <td><div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>{i.status === 'active' ? <>
                <Button type="submit" variant="ghost" size="sm" onClick={() => { setError(null); setKeyFor(i); }}>New key<span className="sr-only"> for {i.name}</span></Button>
                <Button type="submit" variant="ghost" size="sm" onClick={() => { setError(null); setDisabling(i); }}>Disable<span className="sr-only"> {i.name}</span></Button>
              </> : <Button type="submit" variant="ghost" size="sm" disabled={busy} onClick={() => void run(async () => { await api(`/v1/integrations/${i.id}/enable`, { body: {} }); toast(`${i.name} is back: give it a new key`); })}>Bring back<span className="sr-only"> {i.name}</span></Button>}
              </div></td>
            </tr>
          ))}</tbody>
        </Table>
      )}
      <form className="card-pad form-grid" style={{ borderTop: '1px solid var(--border)' }} onSubmit={add}>
        <h3 className="full">Add an integration</h3>
        {!keyFor && !disabling && !revoking && <div className="full"><ErrorBanner error={error} focusOnShow /></div>}
        <Field label="Integration name" required><input className={fieldClass} autoComplete="off" maxLength={100} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
        <Field label="Answers to" required hint="The person accountable for what it does.">
          <select className={selectClass} value={form.answerable_user_id} onChange={(e) => setForm({ ...form, answerable_user_id: e.target.value })}>
            <option value="">Choose a person</option>
            {people.map((u) => <option key={u.id} value={u.id}>{u.display_name}</option>)}
          </select>
        </Field>
        <div className="full"><Button type="submit" variant="secondary" disabled={busy || !form.name.trim() || !form.answerable_user_id}>Add integration</Button></div>
      </form>
      <ConfirmDialog open={!!keyFor} title={`New key for ${keyFor?.name ?? ''}`} confirmLabel="Create key" busy={busy} onConfirm={makeKey} onCancel={() => { setKeyFor(null); setError(null); }}>
        <ErrorBanner error={error} focusOnShow />
        <Field label="Key name" required><input className={fieldClass} autoComplete="off" maxLength={100} value={keyName} onChange={(e) => setKeyName(e.target.value)} /></Field>
        <Field label="Access" hint="Never more than the integration's role allows. It expires in 90 days.">
          <select className={selectClass} value={access} onChange={(e) => setAccess(e.target.value as 'read' | 'propose')}><option value="read">Read only</option><option value="propose">Read and propose</option></select>
        </Field>
      </ConfirmDialog>
      <ConfirmDialog open={!!disabling} title={`Disable ${disabling?.name ?? ''}?`} confirmLabel="Disable integration" tone="danger" busy={busy}
        onConfirm={() => void run(async () => { await api(`/v1/integrations/${disabling!.id}/disable`, { body: {} }); toast('Integration disabled'); setDisabling(null); })} onCancel={() => { setDisabling(null); setError(null); }}>
        <ErrorBanner error={error} focusOnShow />
        <p className="small" style={{ margin: 0 }}>Every key it holds stops working at once. What it did stays in the log. You can bring it back later, with a new key.</p>
      </ConfirmDialog>
      <ConfirmDialog open={!!revoking} title={`Revoke ${revoking?.name ?? ''}?`} confirmLabel="Revoke key" tone="danger" busy={busy}
        onConfirm={() => void run(async () => { await api(`/v1/tokens/${revoking!.id}/revoke`, { body: {} }); toast('Key revoked'); setRevoking(null); })} onCancel={() => { setRevoking(null); setError(null); }}>
        <ErrorBanner error={error} focusOnShow />
        <p className="small" style={{ margin: 0 }}>The next call with this key fails. Make a new key instead.</p>
      </ConfirmDialog>
    </section>
  );
}
